import { useId, useRef, useState } from 'react';
import { Button, Empty, Input, Radio, Space, Tooltip } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import JsonEditor from '@/pages/components/JsonEditor';
import { formatJsonPreservingValues } from '@/lib/jsonValues';

export default function ConfigReader({ value, original, onChange, onReset, label, t }) {
  const [format, setFormat] = useState('formatted');
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState(true);
  const [focused, setFocused] = useState(false);
  const root = useRef();
  const id = useId();
  const dirty = value !== original;
  let text = value;
  if (format === 'formatted') {
    try { text = formatJsonPreservingValues(value); } catch { /* Preserve invalid source text. */ }
  }
  const parts = []; let matches = 0;
  if (query) {
    let offset = 0, at;
    while ((at = text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase(), offset)) >= 0 && matches < 500) {
      parts.push(text.slice(offset, at), <mark key={at}>{text.slice(at, at + query.length)}</mark>);
      offset = at + query.length; matches++;
    }
    parts.push(text.slice(offset));
  }
  const focusReader = () => requestAnimationFrame(() => root.current?.querySelector('pre')?.focus({ preventScroll: true }));
  const renderText = () => value ? <pre id={id} tabIndex={0} aria-label={label} className="json-text"><code>{query ? parts : text}</code></pre>
    : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t.missingConfig} />;
  return <section ref={root} className={`config-reader ${focused ? 'is-focused' : ''}`} aria-label={label}>
    <div className="reader-tools">
      {viewMode && <Radio.Group value={format} onChange={event => { setFormat(event.target.value); setQuery(''); }} size="small" aria-label={t.format}>
        <Radio.Button value="formatted">{t.format}</Radio.Button><Radio.Button value="raw">{t.original}</Radio.Button><Radio.Button value="tree">{t.tree}</Radio.Button>
      </Radio.Group>}
      <Space wrap>
        {dirty && <Button size="small" icon={<UndoOutlined />} onClick={() => { onReset(); focusReader(); }}>{t.resetDraft}</Button>}
        <Tooltip title={focused ? t.collapse : t.expand}><Button size="small" aria-controls={id} aria-label={focused ? t.collapse : t.expand}
          icon={focused ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={() => { setFocused(!focused); focusReader(); }} /></Tooltip>
      </Space>
    </div>
    {viewMode && <Input allowClear prefix={<SearchOutlined />} aria-label={t.searchJson} placeholder={t.searchJson} value={query}
      onChange={event => { setQuery(event.target.value); if (format === 'tree') setFormat('formatted'); }}
      suffix={query ? <span className="match-count">{matches >= 500 ? '500+' : matches}</span> : null} />}
    {viewMode && query && matches === 0 && <p role="status" className="search-empty">{t.searchNone}</p>}
    <p className="config-note" role={dirty ? 'status' : undefined}>{dirty ? t.localDraft : t.localEditing}</p>
    <JsonEditor value={value} onChange={onChange} label={label} initialViewMode height={focused ? '65vh' : 420} maxHeight={focused ? 'none' : 600}
      onModeChange={next => { setViewMode(next); if (next) focusReader(); }} renderView={format === 'tree' && !query ? undefined : renderText} />
  </section>;
}
